import * as signalR from '@microsoft/signalr';
import {HubConnection, HubConnectionBuilder, IHttpConnectionOptions} from '@microsoft/signalr';
import {BehaviorSubject} from 'rxjs';
import {HubState} from '../_models/hub-state';

/** Ritardi (ms) tra i tentativi di riconnessione automatica. L'ultimo valore viene ripetuto all'infinito. */
const RETRY_DELAYS_MS = [0, 3000, 10000, 60000];

/**
 * Numero di tentativi di riconnessione mostrati come "in riconnessione" (arancione) prima di
 * dichiarare l'hub offline (rosso). Superata la soglia il client CONTINUA comunque a ritentare,
 * così il recupero resta automatico quando il backend torna disponibile.
 */
export const HUB_RECONNECT_RED_THRESHOLD = 3;

/** Opzioni di trasporto usate dalla maggior parte degli hub (WebSocket puro, senza negoziazione). */
export const WEBSOCKET_HUB_OPTIONS: IHttpConnectionOptions = {
  skipNegotiation: true,
  transport: signalR.HttpTransportType.WebSockets,
  withCredentials: false,
};

/** Stato da pubblicare al tentativo di riconnessione numero `previousRetryCount` (0-based). */
export function hubReconnectState(previousRetryCount: number): HubState {
  return previousRetryCount < HUB_RECONNECT_RED_THRESHOLD ? HubState.Connecting : HubState.Offline;
}

/** Ritardo (ms) prima del tentativo di riconnessione numero `previousRetryCount`. */
export function hubRetryDelayMs(previousRetryCount: number): number {
  const index = Math.min(previousRetryCount, RETRY_DELAYS_MS.length - 1);
  return RETRY_DELAYS_MS[index];
}

/**
 * Stato aggregato di più hub. Priorità: anche un solo hub offline → rosso; altrimenti anche uno
 * solo in riconnessione → arancione; altrimenti (tutti online, o lista vuota) → verde.
 */
export function aggregateHubState(states: number[]): HubState {
  if (states.some(s => s === HubState.Offline)) {
    return HubState.Offline;
  }
  if (states.some(s => s === HubState.Connecting)) {
    return HubState.Connecting;
  }
  return HubState.Online;
}

/**
 * Costruisce una connessione SignalR con riconnessione automatica e cablaggio dello stato sul
 * `hubState` passato. NON avvia la connessione: registrare i propri handler `.on(...)` e poi
 * chiamare {@link startHubConnection}.
 */
export function buildHubConnection(
  url: string,
  hubState: BehaviorSubject<number>,
  options: IHttpConnectionOptions = WEBSOCKET_HUB_OPTIONS,
  logLevel: signalR.LogLevel = signalR.LogLevel.Warning,
): HubConnection {
  const connection = new HubConnectionBuilder()
    .withUrl(url, options)
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: context => {
        hubState.next(hubReconnectState(context.previousRetryCount));
        return hubRetryDelayMs(context.previousRetryCount);
      },
    })
    .configureLogging(logLevel)
    .build();

  connection.onclose(() => hubState.next(HubState.Offline));
  connection.onreconnected(() => hubState.next(HubState.Online));
  connection.onreconnecting(() => hubState.next(HubState.Connecting));

  return connection;
}

/**
 * Tiene traccia della `start()` in corso per ogni connessione, così {@link stopHubConnection} può
 * attenderne l'esito prima di fermarla. Chiamare `.stop()` mentre `.start()` non è ancora risolta
 * causa in SignalR un AbortError ("Failed to start... before stop() was called" /
 * "connection was stopped during negotiation") — tipicamente quando un logout (es. refresh token
 * scaduto all'avvio dell'app) arriva mentre gli hub si stanno ancora connettendo.
 */
const pendingStarts = new WeakMap<HubConnection, Promise<unknown>>();

/**
 * Avvia la connessione: pubblica `Connecting` (arancione) durante l'handshake e `Online` (verde)
 * al successo. In caso di errore la promise viene rigettata (lo stato resta `Connecting`, la
 * riconnessione automatica non parte sul primo `start` fallito).
 */
export function startHubConnection(connection: HubConnection, hubState: BehaviorSubject<number>): Promise<void> {
  hubState.next(HubState.Connecting);
  const startPromise = connection.start().then(() => {
    hubState.next(HubState.Online);
  });
  pendingStarts.set(connection, startPromise);
  return startPromise;
}

/**
 * Ferma la connessione attendendo prima l'eventuale `start()` in corso, per evitare l'AbortError
 * generato da SignalR quando `stop()` interrompe una connessione ancora in fase di negoziazione.
 */
export async function stopHubConnection(connection?: HubConnection): Promise<void> {
  if (!connection) {
    return;
  }

  await pendingStarts.get(connection)?.catch(() => undefined);
  await connection.stop();
}
